import {
  PasswordInput,
  Button,
  Paper,
  Title,
  Text,
  Stack,
  Center,
  Box,
  Alert,
} from '@mantine/core'
import { useForm } from '@mantine/form'
import { notifications } from '@mantine/notifications'
import { IconAlertCircle } from '@tabler/icons-react'
import { useState } from 'react'
import { useAuthStore } from '../../stores/authStore'
import { apiClient } from '../../api/client'
import { getErrorMessage } from '../../api/authApi'

export function ChangePasswordView() {
  const { clearMustChangePassword } = useAuthStore()
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const form = useForm({
    initialValues: {
      currentPassword: '',
      newPassword: '',
      confirmPassword: '',
    },
    validate: {
      currentPassword: (value) => (value.length < 1 ? 'Current password is required' : null),
      newPassword: (value) => {
        if (value.length < 8) return 'Password must be at least 8 characters'
        if (!/[A-Z]/.test(value)) return 'Password must contain an uppercase letter'
        if (!/[a-z]/.test(value)) return 'Password must contain a lowercase letter'
        if (!/[0-9]/.test(value)) return 'Password must contain a number'
        return null
      },
      confirmPassword: (value, values) =>
        value !== values.newPassword ? 'Passwords do not match' : null,
    },
  })

  const handleSubmit = form.onSubmit(async (values) => {
    setIsLoading(true)
    setError(null)

    try {
      await apiClient.post('/users/me/password', {
        currentPassword: values.currentPassword,
        newPassword: values.newPassword,
      })

      notifications.show({
        title: 'Password Changed',
        message: 'Your password has been updated successfully.',
        color: 'green',
      })

      // Clear the flag - this will cause App.tsx to re-render and show the main app
      clearMustChangePassword()
    } catch (err) {
      setError(getErrorMessage(err))
    } finally {
      setIsLoading(false)
    }
  })

  return (
    <Center style={{ minHeight: '100vh' }}>
      <Box w={400}>
        <Title ta="center" mb="md">
          Change Your Password
        </Title>
        <Text c="dimmed" size="sm" ta="center" mb="xl">
          You must change your password before continuing
        </Text>

        <Paper withBorder shadow="md" p="xl" radius="md">
          {error && (
            <Alert
              icon={<IconAlertCircle size={16} />}
              title="Error"
              color="red"
              mb="md"
              withCloseButton
              onClose={() => setError(null)}
            >
              {error}
            </Alert>
          )}

          <form onSubmit={handleSubmit}>
            <Stack>
              <PasswordInput
                label="Current Password"
                placeholder="Enter your temporary password"
                required
                disabled={isLoading}
                {...form.getInputProps('currentPassword')}
              />
              <PasswordInput
                label="New Password"
                placeholder="Choose a strong password"
                description="Min 8 chars, uppercase, lowercase, and number required"
                required
                disabled={isLoading}
                {...form.getInputProps('newPassword')}
              />
              <PasswordInput
                label="Confirm New Password"
                placeholder="Confirm your new password"
                required
                disabled={isLoading}
                {...form.getInputProps('confirmPassword')}
              />
              <Button type="submit" fullWidth mt="md" loading={isLoading}>
                Change Password
              </Button>
            </Stack>
          </form>
        </Paper>
      </Box>
    </Center>
  )
}
